import { Text } from "@mantine/core";
import {
  IconUsers, IconPhoneCall, IconMail, IconNote, IconCoin, IconUser,
  IconArrowRight, IconRobot, IconNotes, IconHistory, IconUserPlus,
} from "@tabler/icons-react";

// Activity-type → icon/color/label config. Shared by the candidate timeline and the tenant activity feed.
export const entryTypeConfig = {
  meeting:      { tKey: "activity_meeting",       icon: IconUsers,      color: "blue" },
  call:         { tKey: "activity_call",          icon: IconPhoneCall,  color: "teal" },
  email:        { tKey: "activity_email",         icon: IconMail,       color: "indigo" },
  note:         { tKey: "activity_note",          icon: IconNote,       color: "gray" },
  compensation: { tKey: "activity_compensation",  icon: IconCoin,       color: "amber" },
  reference:    { tKey: "activity_reference",     icon: IconUser,       color: "violet" },
  statusChange: { tKey: "activity_status_change", icon: IconArrowRight, color: "cyan" },
  system:       { tKey: "activity_system",        icon: IconRobot,      color: "grape" },
  transcriptInsight: { tKey: "activity_transcript", icon: IconNotes,    color: "orange" },
  fieldChange:  { tKey: "activity_field_change",  icon: IconHistory,    color: "gray" },
  candidateCreated: { tKey: "activity_candidate_created", icon: IconUserPlus, color: "green" },
};

// Automatic, non-note timeline events: field/stage changes, machine/system writes, and the
// "contact added" event. These are the change-tracking entries the candidate timeline hides by
// default so the feed reads as notes; the "show events" toggle reveals them. Manual log entries
// (note/call/meeting/email/reference/compensation/transcriptInsight) are always shown.
export const SYSTEM_EVENT_TYPES = ["system", "fieldChange", "statusChange", "candidateCreated"];
export const isSystemEvent = (type) => SYSTEM_EVENT_TYPES.includes(type);

// Maps a FieldChange fieldName to an existing localized label key (falls back to the raw name).
export const FIELD_LABEL_KEY = {
  name: "name", title: "title", location: "location", email: "email", phone: "phone",
  emails: "email", phoneNumbers: "phone",
  summary: "summary", skills: "skills", languages: "languages", career: "career_history",
  education: "education", linkedInUrl: "linkedin_url", notes: "notes", tags: "tags",
};
export const fieldLabel = (field, t) => t(FIELD_LABEL_KEY[field] || field, { defaultValue: field });

// Machine source kinds → existing localized label key (used when a FieldChange has no human actor).
const SOURCE_KIND_LABEL_KEY = {
  manual: "source_manual", cv: "source_cv", linkedin: "source_linkedin",
  crmNotes: "source_crmNotes", import: "source_import",
};

// The actor for an activity line: the human's name if present, else the machine source
// (CV/LinkedIn/Notes/Import), else "unknown". `t` is the Veft-namespace t().
export function actorLabel(entry, t) {
  if (entry.createdByName) return entry.createdByName;
  const key = SOURCE_KIND_LABEL_KEY[entry.sourceKind];
  return key ? t(key) : t("activity_unknown_user");
}

/**
 * Localized one-line description of an activity entry — shared by the candidate timeline and the
 * tenant activity feed so both render stage transitions and field changes identically. Machine
 * writes read as "LinkedIn changed Title", not "Unknown user changed Title".
 */
export function activityBodyText(entry, t) {
  if (entry.type === "candidateCreated")
    return t("activity_candidate_created_body", { actor: actorLabel(entry, t) });
  if (entry.type === "statusChange" && entry.toStage) {
    const actor = actorLabel(entry, t);
    const toStage = t(`stage_${entry.toStage}`);
    return entry.fromStage
      ? t("activity_status_change_moved", { actor, fromStage: t(`stage_${entry.fromStage}`), toStage })
      : t("activity_status_change_added", { actor, toStage });
  }
  if (entry.type === "fieldChange") {
    const actor = actorLabel(entry, t);
    const field = fieldLabel(entry.fieldName, t);
    // A single enrichment write that touched several career/education entries is collapsed to one line.
    if (entry.groupedCount > 1)
      return t("activity_field_grouped", { actor, field, count: entry.groupedCount });
    return entry.previousValue == null
      ? t("activity_field_added", { actor, field, value: entry.currentValue })
      : entry.currentValue == null
        ? t("activity_field_removed", { actor, field, value: entry.previousValue })
        : t("activity_field_changed", { actor, field, from: entry.previousValue, to: entry.currentValue });
  }
  return entry.content;
}

/**
 * Splits a plain-text string into React nodes, wrapping http(s) URLs in clickable links so notes
 * that paste a bare URL become navigable. Splitting on a capturing group puts the matched URLs at
 * odd indices. React escapes the text nodes for us, so unlike the dangerouslySetInnerHTML path
 * there's no manual HTML-escaping / attribute-breakout XSS concern. Render inside a parent <Text>.
 */
const URL_SPLIT_RE = /(https?:\/\/[^\s<]+)/g;
export function linkifyText(text) {
  if (typeof text !== "string" || !text.includes("http")) return text;
  return text.split(URL_SPLIT_RE).map((part, i) =>
    i % 2 === 1 ? (
      <Text
        key={i}
        component="a"
        inherit
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        c="blue.6"
        style={{ textDecoration: "underline" }}
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </Text>
    ) : (
      part
    ),
  );
}

/**
 * JSX variant of {@link activityBodyText} that renders a field change as a colored diff — the old
 * value struck through in red, the new value in green — so a feed of edits reads as a diff rather
 * than a wall of "X → Y" text. Everything else (notes, stage moves, grouped/machine writes) falls
 * back to the plain localized sentence, with bare URLs linkified. Render inside a parent <Text>.
 */
export function ActivityBody({ entry, t }) {
  if (entry.type === "fieldChange" && !(entry.groupedCount > 1)) {
    const actor = actorLabel(entry, t);
    const field = fieldLabel(entry.fieldName, t);
    const old = <Text span inherit c="red.7" td="line-through">{entry.previousValue}</Text>;
    const neu = <Text span inherit c="green.7">{entry.currentValue}</Text>;
    if (entry.previousValue == null)
      return <>{t("activity_field_added_prefix", { actor, field })}: {neu}</>;
    if (entry.currentValue == null)
      return <>{t("activity_field_removed_prefix", { actor, field })}: {old}</>;
    return <>{t("activity_field_changed_prefix", { actor, field })}: {old} <Text span inherit c="dimmed">→</Text> {neu}</>;
  }
  return linkifyText(activityBodyText(entry, t));
}
